import classNames from 'classnames';
import Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import type { DistributedOmit } from 'type-fest';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './MessageComposer.module.scss';
import type { Tool } from './useAiCopilot';
import Button from '../components/Button';
import Menu, { MenuItem } from '../components/Menu';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import { MOD_KEY } from '../constants/os';
import type { Binding } from '../editor/BaseKeyboard';
import getPostIdsFromOps from './utils/getPostIdsFromOps';
import PostMentionPicker from '../editor/controls/PostMentionPicker';
import QuillComponent from '../editor/QuillComponent';
import { useQuill } from '../editor/QuillContext';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import allPostsIcon from '../icons/non-prefilled/16x16/allPosts.svg';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';
import isContentEmpty from '../post/utils/isContentEmpty';
import { insertPrefix } from '../utils/quillHelpers';
import useSlug from '../utils/router/useSlug';

const SelectableMenuItem = ({
  selected,
  ...props
}: DistributedOmit<
  React.ComponentProps<typeof MenuItem>,
  'right' | 'submenuItems'
> & {
  selected?: boolean;
}) => {
  return (
    <MenuItem
      right={
        <SVGIcon
          size={16}
          className={classNames(styles.selectableMenuItemCheckIcon, {
            [styles.selected]: selected,
          })}
        >
          {checkIcon}
        </SVGIcon>
      }
      {...props}
    />
  );
};

interface ToolsMenuProps {
  children: React.ReactNode;
  selectedTools: Set<Tool>;
  onToggleTool: (tool: Tool) => void;
}

const ToolsMenu = ({
  children,
  selectedTools,
  onToggleTool,
}: ToolsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <PopOverPrimitive
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="top-start"
      offset={6}
    >
      <PopOverPrimitive.Trigger>{children}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content>
          <Menu>
            <SelectableMenuItem
              selected={selectedTools.has('slab')}
              onClick={() => {
                onToggleTool('slab');
              }}
            >
              Slab posts
            </SelectableMenuItem>
            <SelectableMenuItem
              selected={selectedTools.has('web')}
              onClick={() => {
                onToggleTool('web');
              }}
            >
              Web search
            </SelectableMenuItem>
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

interface MessageComposerProps {
  isGenerating: boolean;
  onSend: () => void;
  onStop: () => void;
  tools: Set<Tool>;
  setTools: React.Dispatch<React.SetStateAction<Set<Tool>>>;
  editControl?: {
    onCancel: () => void;
    onEdit: () => void;
  };
}

const MessageComposer = ({
  isGenerating,
  onSend,
  onStop,
  tools,
  setTools,
  editControl,
}: MessageComposerProps) => {
  const { quill } = useQuill();
  const [hasInputContent, setHasInputContent] = useState(false);
  const [postIds, setPostIds] = useState<Set<string>>(() => new Set());

  const [postId] = useSlug('postSlug');

  const onSendEvent = useEffectEvent(onSend);

  useEffect(() => {
    if (!quill) return undefined;

    const handleTextChange = () => {
      const { ops } = quill.getContents();
      setHasInputContent(!isContentEmpty(ops));
      setPostIds(new Set(getPostIdsFromOps(ops)));
    };

    quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) return undefined;

    const binding: Binding = {
      key: 'Enter',
      shiftKey: false,
      [MOD_KEY]: null,
      handler() {
        if (isGenerating) return;
        onSendEvent();
      },
    };

    // @ts-expect-error
    quill.keyboard.prependBinding(binding);

    return () => {
      // @ts-expect-error
      quill.keyboard.removeBinding(binding);
    };
  }, [quill, isGenerating]);

  return (
    <div
      className={classNames(styles.messageComposer, 'copilot-message-composer')}
    >
      <div className={styles.inputContainer}>
        <QuillComponent
          noTitleLine
          namespace="aiCopilotChat"
          contentPlaceholder="Ask about your Slab posts"
          readOnly={false}
          autoFocus
          moduleConfig={{ prefixListener: true }}
        />
        {quill && (
          <PostMentionPicker
            quill={quill}
            postId={postId}
            excludePostIds={postIds}
          />
        )}
      </div>
      <div className={styles.actions}>
        <div className={styles.actionsLeft}>
          <Button
            icon={addIcon}
            onClick={() => {
              if (!quill) return;
              insertPrefix(quill, '+');
            }}
            className={styles.iconButton}
          />

          <ToolsMenu
            selectedTools={tools}
            onToggleTool={(tool) => {
              setTools((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(tool)) {
                  newSet.delete(tool);
                } else {
                  newSet.add(tool);
                }
                return newSet;
              });
            }}
          >
            <Button icon={allPostsIcon} className={styles.iconButton}>
              Tools
            </Button>
          </ToolsMenu>
        </div>
        <div className={styles.actionsRight}>
          {editControl ? (
            <>
              <Button
                size="small"
                variant="secondary"
                onClick={editControl.onCancel}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="primary"
                disabled={!hasInputContent}
                onClick={editControl.onEdit}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              {isGenerating ? (
                <Button
                  size="small"
                  variant="primary"
                  className={styles.stopButton}
                  onClick={onStop}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="primary"
                  disabled={!hasInputContent}
                  onClick={onSend}
                >
                  Send
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;
